import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import { useNavigate } from 'react-router-dom';
import { Shield, Users, FileText, Activity, Trash2, RefreshCw, ChevronDown, MessageCircle, X, FileCheck, TrendingUp, MapPin, Heart, UserCheck, Clock, Send, History } from 'lucide-react';
import ChatPanel from '../components/lifelink/ChatPanel';
import { PriorityBadge, StatusBadge } from '../components/lifelink/PriorityBadge';
import { CATEGORY_CONFIG } from '../components/lifelink/CategoryGrid';
import AdminDocuments from '../components/lifelink/AdminDocuments';
import AdminAnalytics from '../components/lifelink/AdminAnalytics';
import AdminGeozones from '../components/lifelink/AdminGeozones';
import AdminCheckinAlerts from '../components/lifelink/AdminCheckinAlerts';
import AdminPendingUsers from '../components/lifelink/AdminPendingUsers';
import AdminAuditLog from '../components/lifelink/AdminAuditLog';
import axios from 'axios';
import { toast } from 'sonner';
import useSEO from '../hooks/useSEO';
import { getCountryByCode } from '../utils/countries';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AdminPage() {
  const { user } = useAuth();
  const { t } = useLanguage();
  const navigate = useNavigate();
  useSEO({ title: 'Pannello Admin', description: 'Area riservata amministratori LIFE LINKE', noindex: true });
  const [overview, setOverview] = useState(null);
  const [users, setUsers] = useState([]);
  const [usersTotal, setUsersTotal] = useState(0);
  const [requests, setRequests] = useState([]);
  const [reqTotal, setReqTotal] = useState(0);
  const [activeTab, setActiveTab] = useState('overview');
  const [loading, setLoading] = useState(true);
  const [roleFilter, setRoleFilter] = useState('');
  const [chatRequest, setChatRequest] = useState(null);

  const token = localStorage.getItem('lifelink_token');
  const headers = { Authorization: `Bearer ${token}` };

  useEffect(() => {
    if (!user || user.role !== 'admin') {
      navigate('/');
    }
  }, [user, navigate]);

  const fetchOverview = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/admin/overview`, { headers });
      setOverview(data);
    } catch (e) {
      console.error(e);
    }
  }, []);

  const fetchUsers = useCallback(async () => {
    try {
      const params = roleFilter ? { role: roleFilter, limit: 100 } : { limit: 100 };
      const { data } = await axios.get(`${API}/admin/users`, { headers, params });
      setUsers(data.users || []);
      setUsersTotal(data.total || 0);
    } catch (e) {
      console.error(e);
    }
  }, [roleFilter]);

  const fetchRequests = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/requests`, { params: { limit: 50 }, headers });
      setRequests(data.requests || []);
      setReqTotal(data.total || 0);
    } catch (e) {
      console.error(e);
    }
  }, []);

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      await Promise.all([fetchOverview(), fetchUsers(), fetchRequests()]);
      setLoading(false);
    };
    load();
  }, [fetchOverview, fetchUsers, fetchRequests]);

  useEffect(() => { fetchUsers(); }, [roleFilter, fetchUsers]);

  const updateRole = async (email, newRole) => {
    try {
      await axios.put(`${API}/admin/users/${email}/role`, { role: newRole }, { headers });
      toast.success(`Ruolo di ${email} aggiornato a ${newRole}`);
      fetchUsers();
      fetchOverview();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Errore aggiornamento ruolo');
    }
  };

  const deleteUser = async (email) => {
    if (!window.confirm(`Sei sicuro di voler eliminare ${email}?`)) return;
    try {
      await axios.delete(`${API}/admin/users/${email}`, { headers });
      toast.success(`Utente ${email} eliminato`);
      fetchUsers();
      fetchOverview();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Errore eliminazione');
    }
  };

  const autoAssign = async (id) => {
    try {
      const { data } = await axios.post(`${API}/requests/${id}/auto-assign`, {}, { headers });
      toast.success(`Assegnata a ${data.operatore}`);
      fetchRequests();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Errore auto-assegnazione');
    }
  };

  const deleteRequest = async (id) => {
    if (!window.confirm('Sei sicuro di voler eliminare questa richiesta?')) return;
    try {
      await axios.delete(`${API}/admin/requests/${id}`, { headers });
      toast.success('Richiesta eliminata');
      fetchRequests();
      fetchOverview();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Errore eliminazione');
    }
  };

  const dispatchToService = async (id) => {
    if (!window.confirm('Inoltrare ai servizi di emergenza (112/118/115/Protezione Civile)? Verrà eseguito un dispatch tramite webhook.')) return;
    try {
      const { data } = await axios.post(`${API}/emergency-services/dispatch/${id}`, { note: 'Inoltro manuale da pannello admin' }, { headers });
      toast.success(`Inoltrata a ${data.dispatch?.service_name} (${data.dispatch?.service_tel}) · ${data.delivery_status}`);
      fetchRequests();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Errore inoltro');
    }
  };

  if (!user || user.role !== 'admin') return null;

  const tabs = [
    { id: 'overview', label: 'Panoramica', icon: Activity },
    { id: 'pending', label: 'Da Approvare', icon: Clock },
    { id: 'chat', label: 'Chat', icon: MessageCircle },
    { id: 'users', label: 'Utenti', icon: Users },
    { id: 'requests', label: 'Richieste', icon: FileText },
    { id: 'documents', label: 'Documenti', icon: FileCheck },
    { id: 'analytics', label: 'Analytics', icon: TrendingUp },
    { id: 'geozones', label: 'Zone Geo', icon: MapPin },
    { id: 'checkin', label: 'Check-in', icon: Heart },
    { id: 'audit', label: 'Audit Log', icon: History },
  ];

  return (
    <div className="min-h-screen bg-[#E8F4FD] pt-20 pb-8" data-testid="admin-page">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-[#FF3B30] flex items-center justify-center">
              <Shield size={20} className="text-white" />
            </div>
            <div>
              <h1 className="font-['Barlow_Condensed'] text-2xl sm:text-3xl font-black uppercase tracking-tight text-[#0D1B2A]">
                Gestione Servizi LIFE LINKE
              </h1>
              <p className="text-xs text-[#4A6B8A] uppercase tracking-widest">Pannello Amministrazione</p>
            </div>
          </div>
          <button
            onClick={() => { fetchOverview(); fetchUsers(); fetchRequests(); }}
            className="p-2 border border-[#B8D4E8] hover:border-[#FF3B30] transition-all bg-white"
            data-testid="admin-refresh-btn"
          >
            <RefreshCw size={16} className="text-[#4A6B8A]" />
          </button>
        </div>

        {/* Tabs */}
        <div className="flex gap-1 mb-6 border-b border-[#B8D4E8] overflow-x-auto" data-testid="admin-tabs">
          {tabs.map(({ id, label, icon: Icon }) => (
            <button
              key={id}
              onClick={() => setActiveTab(id)}
              className={`flex items-center gap-2 px-4 py-3 text-xs font-bold uppercase tracking-widest transition-all border-b-2 ${
                activeTab === id
                  ? 'border-[#FF3B30] text-[#FF3B30] bg-white'
                  : 'border-transparent text-[#4A6B8A] hover:text-[#0D1B2A]'
              }`}
              data-testid={`admin-tab-${id}`}
            >
              <Icon size={14} />
              {label}
            </button>
          ))}
        </div>

        {loading ? (
          <div className="text-center py-16 text-[#4A6B8A]">Caricamento...</div>
        ) : (
          <>
            {/* Overview Tab */}
            {activeTab === 'overview' && overview && (
              <div className="space-y-6" data-testid="admin-overview">
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  {[
                    { label: 'Utenti Totali', value: overview.total_users, color: '#0A84FF' },
                    { label: 'Richieste Totali', value: overview.total_requests, color: '#FF3B30' },
                    { label: 'Check-in', value: overview.total_checkins, color: '#32D74B' },
                    { label: 'File Caricati', value: overview.total_files, color: '#FF9F0A' },
                  ].map(({ label, value, color }) => (
                    <div key={label} className="border border-[#B8D4E8] bg-white p-5" data-testid={`admin-stat-${label}`}>
                      <span className="text-[10px] text-[#4A6B8A] uppercase tracking-widest font-bold">{label}</span>
                      <div className="text-3xl font-['JetBrains_Mono'] font-bold mt-1" style={{ color }}>{value}</div>
                    </div>
                  ))}
                </div>

                {/* Users by role */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="border border-[#B8D4E8] bg-white p-5">
                    <h3 className="text-xs font-bold uppercase tracking-widest text-[#4A6B8A] mb-4">Utenti per Ruolo</h3>
                    {Object.entries(overview.users_by_role || {}).map(([role, count]) => (
                      <div key={role} className="flex items-center justify-between py-2 border-b border-[#E8F4FD] last:border-0">
                        <span className="text-sm text-[#0D1B2A] uppercase font-bold">{role}</span>
                        <span className="text-sm font-mono text-[#FF3B30]">{count}</span>
                      </div>
                    ))}
                  </div>
                  <div className="border border-[#B8D4E8] bg-white p-5">
                    <h3 className="text-xs font-bold uppercase tracking-widest text-[#4A6B8A] mb-4">Richieste per Stato</h3>
                    {Object.entries(overview.requests_by_status || {}).map(([stato, count]) => {
                      const colors = { nuova: '#FF3B30', in_corso: '#FF9F0A', assegnata: '#0A84FF', risolta: '#32D74B', chiusa: '#6B8AB5' };
                      return (
                        <div key={stato} className="flex items-center justify-between py-2 border-b border-[#E8F4FD] last:border-0">
                          <span className="text-sm text-[#0D1B2A] uppercase font-bold">{t(stato)}</span>
                          <span className="text-sm font-mono" style={{ color: colors[stato] || '#0D1B2A' }}>{count}</span>
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>
            )}

            {/* Chat Tab */}
            {activeTab === 'chat' && (
              <div className="space-y-4" data-testid="admin-chat">
                <div className="flex items-center gap-3 mb-2">
                  <MessageCircle size={16} className="text-[#FF3B30]" />
                  <span className="text-xs font-bold uppercase tracking-widest text-[#0D1B2A]">Chat con i richiedenti</span>
                  <span className="text-xs text-[#4A6B8A] uppercase tracking-widest">{requests.length} richieste</span>
                </div>
                <p className="text-xs text-[#4A6B8A] mb-4">Seleziona una richiesta per aprire la chat in tempo reale con il richiedente.</p>

                <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                  {/* Request list */}
                  <div className="space-y-2 max-h-[600px] overflow-y-auto">
                    {requests.map((req) => {
                      const catConfig = CATEGORY_CONFIG.find(c => c.id === req.categoria);
                      const Icon = catConfig?.icon;
                      const color = catConfig?.color || '#4A6B8A';
                      const isActive = chatRequest?.id === req.id;
                      return (
                        <button
                          key={req.id}
                          onClick={() => setChatRequest(req)}
                          className={`w-full text-left border p-3 transition-all ${
                            isActive ? 'border-[#FF3B30] bg-[#FF3B30]/5' : 'border-[#B8D4E8] bg-white hover:border-[#FF3B30]/40'
                          }`}
                          data-testid={`chat-select-${req.id}`}
                        >
                          <div className="flex items-center gap-2">
                            <div style={{ color }} className="flex-shrink-0">
                              {Icon && <Icon size={18} />}
                            </div>
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2 flex-wrap">
                                <span className="text-xs font-bold text-[#0D1B2A] uppercase">{t(req.categoria)}</span>
                                <PriorityBadge priority={req.priorita} />
                                <StatusBadge status={req.stato} />
                                {req.chat_attiva && (
                                  <span className="flex items-center gap-1 px-1.5 py-0.5 bg-[#32D74B]/10 border border-[#32D74B]/30">
                                    <div className="w-1.5 h-1.5 bg-[#32D74B] rounded-full animate-pulse" />
                                    <span className="text-[8px] text-[#32D74B] font-mono uppercase">Chat attiva</span>
                                  </span>
                                )}
                              </div>
                              <p className="text-[10px] text-[#4A6B8A] truncate mt-0.5">{req.descrizione}</p>
                              <div className="flex items-center gap-2 mt-0.5">
                                <span className="text-[9px] text-[#6B8AB5]">{req.nome_richiedente || (req.anonimo ? 'Anonimo' : '-')}</span>
                                <span className="text-[9px] text-[#B8D4E8]">{new Date(req.created_at).toLocaleDateString('it-IT')}</span>
                              </div>
                            </div>
                          </div>
                        </button>
                      );
                    })}
                    {requests.length === 0 && (
                      <div className="text-center py-8 border border-[#B8D4E8] bg-white">
                        <MessageCircle size={32} className="mx-auto text-[#B8D4E8] mb-2" />
                        <p className="text-xs text-[#4A6B8A]">Nessuna richiesta disponibile</p>
                      </div>
                    )}
                  </div>

                  {/* Chat panel */}
                  <div className="border border-[#B8D4E8] bg-white min-h-[500px] flex flex-col">
                    {chatRequest ? (
                      <>
                        <div className="p-3 border-b border-[#B8D4E8] flex items-center justify-between bg-[#F0F7FC]">
                          <div className="flex items-center gap-2">
                            {(() => {
                              const cat = CATEGORY_CONFIG.find(c => c.id === chatRequest.categoria);
                              const CatIcon = cat?.icon;
                              return CatIcon ? <CatIcon size={16} style={{ color: cat.color }} /> : null;
                            })()}
                            <div>
                              <span className="text-xs font-bold text-[#0D1B2A] uppercase">{t(chatRequest.categoria)}</span>
                              <span className="text-[9px] text-[#4A6B8A] ml-2">{chatRequest.nome_richiedente || 'Anonimo'}</span>
                            </div>
                          </div>
                          <button onClick={() => setChatRequest(null)} className="p-1 text-[#4A6B8A] hover:text-[#FF3B30]">
                            <X size={14} />
                          </button>
                        </div>
                        <div className="flex-1">
                          <ChatPanel requestId={chatRequest.id} />
                        </div>
                      </>
                    ) : (
                      <div className="flex-1 flex items-center justify-center">
                        <div className="text-center">
                          <MessageCircle size={48} className="mx-auto text-[#B8D4E8] mb-3" />
                          <p className="text-sm text-[#4A6B8A] font-bold">Seleziona una richiesta</p>
                          <p className="text-xs text-[#6B8AB5] mt-1">per avviare la chat in tempo reale</p>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )}

            {/* Users Tab */}
            {activeTab === 'users' && (
              <div className="space-y-4" data-testid="admin-users">
                <div className="flex items-center gap-3">
                  <span className="text-xs text-[#4A6B8A] uppercase tracking-widest">{usersTotal} utenti</span>
                  <select
                    value={roleFilter}
                    onChange={(e) => setRoleFilter(e.target.value)}
                    className="bg-white border border-[#B8D4E8] text-[#0D1B2A] p-2 text-xs uppercase tracking-widest focus:border-[#FF3B30] focus:outline-none"
                    data-testid="admin-role-filter"
                  >
                    <option value="">Tutti i ruoli</option>
                    <option value="civile">Civile</option>
                    <option value="operatore">Operatore</option>
                    <option value="admin">Admin</option>
                  </select>
                </div>

                <div className="border border-[#B8D4E8] bg-white overflow-hidden">
                  <table className="w-full" data-testid="users-table">
                    <thead>
                      <tr className="bg-[#E8F4FD]">
                        <th className="text-left px-4 py-3 text-[10px] text-[#4A6B8A] uppercase tracking-widest font-bold">Nome</th>
                        <th className="text-left px-4 py-3 text-[10px] text-[#4A6B8A] uppercase tracking-widest font-bold">Email</th>
                        <th className="text-left px-4 py-3 text-[10px] text-[#4A6B8A] uppercase tracking-widest font-bold">Codice Fiscale</th>
                        <th className="text-left px-4 py-3 text-[10px] text-[#4A6B8A] uppercase tracking-widest font-bold">Ruolo</th>
                        <th className="text-right px-4 py-3 text-[10px] text-[#4A6B8A] uppercase tracking-widest font-bold">Azioni</th>
                      </tr>
                    </thead>
                    <tbody>
                      {users.map((u, i) => (
                        <tr key={i} className="border-t border-[#E8F4FD] hover:bg-[#F0F8FF]">
                          <td className="px-4 py-3 text-sm text-[#0D1B2A] font-bold">{u.nome} {u.cognome}</td>
                          <td className="px-4 py-3 text-sm text-[#4A6B8A]">{u.email}</td>
                          <td className="px-4 py-3 text-xs font-mono text-[#4A6B8A]">
                            {u.numero_passaporto
                              ? (() => {
                                  const c = getCountryByCode(u.paese_passaporto);
                                  return <span>{c ? c.flag : '📔'} {u.numero_passaporto}</span>;
                                })()
                              : u.codice_iss
                                ? <span><span className="text-[#0A84FF]">🇸🇲</span> {u.codice_iss}</span>
                                : (u.codice_fiscale || '-')}
                          </td>
                          <td className="px-4 py-3">
                            <div className="relative inline-block">
                              <select
                                value={u.role}
                                onChange={(e) => updateRole(u.email, e.target.value)}
                                className={`appearance-none pr-6 pl-2 py-1 text-[10px] uppercase tracking-widest font-bold border cursor-pointer ${
                                  u.role === 'admin' ? 'bg-[#FF3B30]/10 border-[#FF3B30]/30 text-[#FF3B30]' :
                                  u.role === 'operatore' ? 'bg-[#0A84FF]/10 border-[#0A84FF]/30 text-[#0A84FF]' :
                                  'bg-[#32D74B]/10 border-[#32D74B]/30 text-[#32D74B]'
                                }`}
                                data-testid={`role-select-${u.email}`}
                              >
                                <option value="civile">Civile</option>
                                <option value="operatore">Operatore</option>
                                <option value="admin">Admin</option>
                              </select>
                              <ChevronDown size={10} className="absolute right-1 top-1/2 -translate-y-1/2 pointer-events-none text-[#4A6B8A]" />
                            </div>
                          </td>
                          <td className="px-4 py-3 text-right">
                            <button
                              onClick={() => deleteUser(u.email)}
                              className="p-1.5 text-[#6B8AB5] hover:text-[#FF3B30] hover:bg-[#FF3B30]/10 transition-all"
                              data-testid={`delete-user-${u.email}`}
                            >
                              <Trash2 size={14} />
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {/* Requests Tab */}
            {activeTab === 'requests' && (
              <div className="space-y-4" data-testid="admin-requests">
                <span className="text-xs text-[#4A6B8A] uppercase tracking-widest">{reqTotal} richieste totali</span>
                <div className="border border-[#B8D4E8] bg-white overflow-hidden">
                  <table className="w-full" data-testid="requests-table">
                    <thead>
                      <tr className="bg-[#E8F4FD]">
                        <th className="text-left px-4 py-3 text-[10px] text-[#4A6B8A] uppercase tracking-widest font-bold">ID</th>
                        <th className="text-left px-4 py-3 text-[10px] text-[#4A6B8A] uppercase tracking-widest font-bold">Categoria</th>
                        <th className="text-left px-4 py-3 text-[10px] text-[#4A6B8A] uppercase tracking-widest font-bold">Priorità</th>
                        <th className="text-left px-4 py-3 text-[10px] text-[#4A6B8A] uppercase tracking-widest font-bold">Stato</th>
                        <th className="text-left px-4 py-3 text-[10px] text-[#4A6B8A] uppercase tracking-widest font-bold">Richiedente</th>
                        <th className="text-left px-4 py-3 text-[10px] text-[#4A6B8A] uppercase tracking-widest font-bold">Data</th>
                        <th className="text-right px-4 py-3 text-[10px] text-[#4A6B8A] uppercase tracking-widest font-bold">Azioni</th>
                      </tr>
                    </thead>
                    <tbody>
                      {requests.map((r) => {
                        const pColors = { critica: '#FF3B30', alta: '#FF9F0A', media: '#FFD60A', bassa: '#32D74B' };
                        return (
                          <tr key={r.id} className="border-t border-[#E8F4FD] hover:bg-[#F0F8FF]">
                            <td className="px-4 py-3 text-[10px] font-mono text-[#6B8AB5]">{r.id?.substring(0, 8)}</td>
                            <td className="px-4 py-3 text-xs text-[#0D1B2A] uppercase font-bold">{t(r.categoria)}</td>
                            <td className="px-4 py-3">
                              <span className="px-2 py-0.5 text-[10px] font-bold uppercase" style={{ color: pColors[r.priorita], backgroundColor: `${pColors[r.priorita]}15` }}>
                                {t(r.priorita)}
                              </span>
                            </td>
                            <td className="px-4 py-3 text-xs text-[#4A6B8A] uppercase">{t(r.stato)}</td>
                            <td className="px-4 py-3 text-xs text-[#4A6B8A]">{r.anonimo ? 'Anonimo' : (r.nome_richiedente || '-')}</td>
                            <td className="px-4 py-3 text-[10px] text-[#6B8AB5]">{new Date(r.created_at).toLocaleString('it-IT')}</td>
                            <td className="px-4 py-3 text-right">
                              <div className="flex items-center gap-1 justify-end">
                                {!r.operatore_assegnato && (
                                  <button
                                    onClick={() => autoAssign(r.id)}
                                    className="p-1.5 text-[#0A84FF] hover:bg-[#0A84FF]/10 transition-all"
                                    title="Auto-assegna all'operatore con meno carico"
                                    data-testid={`auto-assign-${r.id}`}
                                  >
                                    <UserCheck size={14} />
                                  </button>
                                )}
                                <button
                                  onClick={() => dispatchToService(r.id)}
                                  className="p-1.5 text-[#FF9F0A] hover:bg-[#FF9F0A]/10 transition-all"
                                  title="Inoltra a 112/118/Protezione Civile"
                                  data-testid={`dispatch-${r.id}`}
                                >
                                  <Send size={14} />
                                </button>
                                <button
                                  onClick={() => deleteRequest(r.id)}
                                  className="p-1.5 text-[#6B8AB5] hover:text-[#FF3B30] hover:bg-[#FF3B30]/10 transition-all"
                                  data-testid={`delete-req-${r.id}`}
                                >
                                  <Trash2 size={14} />
                                </button>
                              </div>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
            {/* Pending Users Tab */}
            {activeTab === 'pending' && <AdminPendingUsers />}

            {/* Documents Tab */}
            {activeTab === 'documents' && <AdminDocuments />}

            {/* Analytics Tab */}
            {activeTab === 'analytics' && <AdminAnalytics />}

            {/* Geozones Tab */}
            {activeTab === 'geozones' && <AdminGeozones />}

            {/* Check-in Tab */}
            {activeTab === 'checkin' && <AdminCheckinAlerts />}

            {/* Audit Log Tab */}
            {activeTab === 'audit' && <AdminAuditLog />}
          </>
        )}
      </div>
    </div>
  );
}
